import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  WarningBanner,
  DepartmentClearanceCard,
  DeptClearanceDetailModal,
  useConfirm,
} from "./shared/components";
import { NoDuesFormSections } from "./shared/NoDuesFormSections";
import {
  getNoDuesDepartmentsConfig,
  getNoDuesDispatchList,
  dispatchAllNoDues,
  getNoDuesFormDetails,
  getResignationDetails,
  getNoDuesSettlementStatus,
  sendNoDuesSettlement,
  downloadNoDuesSettlementReport,
  getNoDuesDeptDetail,
  NoDuesDepartmentConfig,
  NoDuesDispatchRow,
  NoDuesSettlementStatus,
  NoDuesDeptChecklistItem,
} from "./services/eSeparation.service";

const DISPATCH_UI_STATUS: Record<string, "Cleared" | "Pending" | "Dues Found"> = {
  CLEARED: "Cleared",
  PENDING: "Pending",
  REJECTED: "Dues Found",
};

const NoDuesDispatch: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirm, confirmDialog } = useConfirm();

  const routeReqId = (location.state as any)?.request_id as string | undefined;

  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);
  const [ndRequestId, setNdRequestId] = useState(routeReqId || "");
  const [resignation, setResignation] = useState<any>(null);
  // The employee's raw submitted No-Dues form payload (esep_no_dues.details),
  // kept separate from `resignation` above — that's a merge of this with the
  // resignation record where same-named fields (e.g. mobile) would otherwise
  // silently take the resignation's value instead of what was actually
  // submitted on the No-Dues form.
  const [ndForm, setNdForm] = useState<any>(null);
  const [departments, setDepartments] = useState<NoDuesDepartmentConfig[]>([]);
  const [dispatches, setDispatches] = useState<NoDuesDispatchRow[]>([]);
  const [comments, setComments] = useState("");

  // Consolidated recovery settlement.
  const [settlement, setSettlement] = useState<NoDuesSettlementStatus | null>(null);
  const [settlementComments, setSettlementComments] = useState("");
  const [sendingSettlement, setSendingSettlement] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  // Department clearance detail modal.
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailDept, setDetailDept] = useState("");
  const [detailItems, setDetailItems] = useState<NoDuesDeptChecklistItem[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    if (!routeReqId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const ndRes = await getNoDuesFormDetails(routeReqId);
      const ndRecord = ndRes.data;
      const resolvedId = ndRecord?.nodues_request_id || routeReqId;
      const parentReqId =
        ndRecord?.request_id || ndRecord?.orig_request_id || routeReqId;
      setNdRequestId(resolvedId);

      const [deptRes, dispatchRes, detailRes, settlementRes] = await Promise.all([
        getNoDuesDepartmentsConfig(),
        getNoDuesDispatchList(resolvedId),
        getResignationDetails(parentReqId),
        getNoDuesSettlementStatus(resolvedId),
      ]);
      setDepartments((deptRes.data || []).filter((d) => d.is_active && !d.is_settlement));
      setDispatches(dispatchRes.data || []);
      const formDetails = ndRecord?.details || {};
      setNdForm(formDetails);
      const resignationData = detailRes.data || null;
      setResignation(
        resignationData
          ? { ...formDetails, ...resignationData }
          : Object.keys(formDetails).length
          ? formDetails
          : null
      );
      setSettlement(settlementRes.data || null);
    } catch {
      toast.error("Failed to load No-Dues dispatch data");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeReqId]);

  const refreshStatus = async (reqId: string) => {
    try {
      const [dispatchRes, settlementRes] = await Promise.all([
        getNoDuesDispatchList(reqId),
        getNoDuesSettlementStatus(reqId),
      ]);
      setDispatches(dispatchRes.data || []);
      setSettlement(settlementRes.data || null);
    } catch {
      // silent — next poll tick will retry
    }
  };

  useEffect(() => {
    if (!ndRequestId || settlement?.settlement?.status === "CLOSED") return;
    const interval = setInterval(() => refreshStatus(ndRequestId), 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ndRequestId, settlement?.settlement?.status]);

  const alreadyDispatchedIds = new Set(dispatches.map((d) => d.department_id));
  const dispatchableDepts = departments.filter((d) => !alreadyDispatchedIds.has(d.id));

  const openDeptDetail = async (row: NoDuesDispatchRow) => {
    setDetailDept(row.department_name);
    setDetailItems([]);
    setDetailOpen(true);
    setDetailLoading(true);
    const res = await getNoDuesDeptDetail(row.id);
    setDetailItems(res.data?.checklist_items || []);
    setDetailLoading(false);
  };

  const handleDispatchAll = async () => {
    if (
      !(await confirm(
        `Send this No-Dues to all ${dispatchableDepts.length} active department(s) for clearance?`,
        { title: "Send to All Departments", confirmLabel: "Send" }
      ))
    )
      return;
    setDispatching(true);
    const res = await dispatchAllNoDues({
      request_id: ndRequestId,
      comments: comments.trim(),
    });
    setDispatching(false);
    if (res.success) {
      toast.success("No-Dues dispatched to all active departments");
      setComments("");
      load();
    } else {
      toast.error(res.message || "Failed to dispatch No-Dues");
    }
  };

  const handleSendSettlement = async () => {
    const dueAmt = Number(settlement?.total_due || 0);
    if (
      !(await confirm(
        dueAmt > 0
          ? `Send the consolidated recovery of ₹${dueAmt.toFixed(2)} to the settlement department?`
          : "Send this No-Dues to the settlement department for its own record and sign-off? There is nothing to recover.",
        { title: "Send Consolidated Report", confirmLabel: "Send" }
      ))
    )
      return;
    setSendingSettlement(true);
    const res = await sendNoDuesSettlement({
      request_id: ndRequestId,
      comments: settlementComments.trim(),
    });
    setSendingSettlement(false);
    if (res.success) {
      toast.success("Consolidated report sent for settlement");
      setSettlementComments("");
      load();
    } else {
      toast.error(res.message || "Failed to send consolidated report");
    }
  };

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    const res = await downloadNoDuesSettlementReport(ndRequestId);
    setDownloadingReport(false);
    if (res.success) toast.success("Settlement report downloaded");
    else toast.error(res.message || "Failed to download report");
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <div className="text-muted small mt-2">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-5">
        {/* Breadcrumbs & Page Header */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-1 small">
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(all_routes.Esepration.hub);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    E-Separation
                  </a>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  No-Dues Dispatch
                </li>
              </ol>
            </nav>
            <h4 className="fw-bold text-dark mb-0">No-Dues Dispatch</h4>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Exit
            </button>
          </div>
        </div>

        {/* Sub-header: employee + No-Dues request */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body py-3 px-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <span
                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 44, height: 44, borderRadius: 10, background: "#FFF3EC", color: "#BA7517" }}
                >
                  <i className="ti ti-send" style={{ fontSize: 22 }} />
                </span>
                <div>
                  <div
                    style={{
                      fontSize: "17px",
                      fontWeight: 700,
                      color: "#344054",
                      lineHeight: "1.3",
                    }}
                  >
                    {resignation?.emp_name || "No-Dues Request"}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#667085",
                      lineHeight: "1.4",
                      marginTop: "2px",
                    }}
                  >
                    {ndRequestId}
                    {resignation?.emp_no ? ` • ${resignation.emp_no}` : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTIONS 01-04: the employee's own submitted No-Dues form,
            rendered identically to how they filled it in. */}
        {ndForm ? (
          <NoDuesFormSections form={ndForm} startNumber={1} employee={resignation} />
        ) : (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  01
                </span>
                No-Dues Form
              </h5>
            </div>
            <div className="card-body p-4">
              <WarningBanner type="info" message="Unable to load form details." />
            </div>
          </div>
        )}

        {/* SECTION 06: DEPARTMENT CLEARANCE STATUS */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  06
                </span>
                Department Clearance Status
              </h5>
              <div className="text-muted small mt-1 ms-4 ps-2">
                {dispatches.length > 0
                  ? "Live status of each department this No-Dues was sent to — updates automatically."
                  : "Not yet dispatched to any department."}
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {dispatches.length === 0 ? (
              <WarningBanner type="info" message="No departments dispatched yet." />
            ) : (
              <div className="row g-3">
                {dispatches.map((d) => (
                  <div key={d.id} className="col-lg-6 col-md-12" style={{ cursor: "pointer" }} onClick={() => openDeptDetail(d)}>
                    <DepartmentClearanceCard
                      deptName={d.department_name}
                      nodalOfficer={d.approver_emp_id || ""}
                      status={DISPATCH_UI_STATUS[String(d.status).toUpperCase()] || "Pending"}
                      remarks={d.remarks || undefined}
                      recoveryAmount={d.recovery_amount ? Number(d.recovery_amount) : undefined}
                      delegatedTo={
                        d.delegated_to_emp_id
                          ? d.delegated_to_name || d.delegated_to_emp_id
                          : undefined
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* SECTION 07: SEND TO DEPARTMENTS */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  07
                </span>
                Send to Departments
              </h5>
              <div className="text-muted small mt-1 ms-4 ps-2">
                Sends this No-Dues to every active clearing department at once.
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {settlement?.settlement ? (
              <WarningBanner
                type="info"
                message="You have sent the No-Dues consolidated report for settlement. Dispatching to departments is closed."
              />
            ) : dispatchableDepts.length === 0 ? (
              <WarningBanner type="success" message="All active departments have already been dispatched." />
            ) : (
              <>
                <div className="row g-3 mb-4">
                  {dispatchableDepts.map((d) => (
                    <div key={d.id} className="col-lg-4 col-md-6 col-sm-12">
                      <div
                        className="p-3 rounded-3 border bg-light h-100 d-flex align-items-center justify-content-between gap-2"
                        style={{ borderColor: "#e9ecef" }}
                      >
                        <div className="d-flex align-items-center gap-2 overflow-hidden">
                          <span
                            className="d-inline-flex align-items-center justify-content-center flex-shrink-0 bg-white rounded-2 shadow-sm text-primary"
                            style={{ width: 32, height: 32 }}
                          >
                            <i className="ti ti-building" style={{ fontSize: 16 }} />
                          </span>
                          <div className="text-truncate">
                            <div className="fw-semibold text-dark small text-truncate" title={d.name}>
                              {d.name}
                            </div>
                            <div className="text-muted" style={{ fontSize: 11 }}>
                              {d.approver_emp_id ? `Approver: ${d.approver_emp_id}` : "No approver set"}
                            </div>
                          </div>
                        </div>
                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-2 py-1 flex-shrink-0" style={{ fontSize: 10 }}>
                          Ready
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Dispatch Comments (optional)
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Any instructions for the departments..."
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                  />
                </div>

                <button
                  className="btn btn-primary d-flex align-items-center justify-content-center gap-2 px-4 py-2"
                  style={{ height: 44, borderRadius: 10 }}
                  onClick={handleDispatchAll}
                  disabled={dispatching}
                >
                  {dispatching ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <i className="ti ti-send" />
                  )}
                  <span>Send to All Departments ({dispatchableDepts.length})</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* SECTION 08: CONSOLIDATED RECOVERY SETTLEMENT */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  08
                </span>
                Consolidated Recovery Settlement
              </h5>
              <div className="text-muted small mt-1 ms-4 ps-2">
                Once every department has acted, send all recoverable dues to the settlement department as one consolidated report.
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {settlement?.settlement ? (
              <>
                <div className="row g-3 mb-4">
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">Settlement Dept</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={settlement.settlement.department_name}
                      readOnly
                    />
                  </div>

                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">Status</label>
                    <div className="pt-1">
                      <span
                        className={`badge ${
                          settlement.settlement.status === "CLOSED"
                            ? "bg-success"
                            : "bg-warning text-dark"
                        }`}
                      >
                        {settlement.settlement.status === "CLOSED" ? "Closed" : "In Progress"}
                      </span>
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">Total Dues</label>
                    <input
                      type="text"
                      className="form-control bg-light fw-bold text-danger"
                      value={`₹ ${Number(settlement.settlement.total_due).toFixed(2)}`}
                      readOnly
                    />
                  </div>

                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">Collected</label>
                    <input
                      type="text"
                      className="form-control bg-light fw-bold text-success"
                      value={`₹ ${Number(settlement.settlement.total_collected).toFixed(2)}`}
                      readOnly
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold text-secondary small mb-2">
                    Department-wise recovery
                  </label>
                  <div className="d-flex flex-column gap-2">
                    {settlement.items.map((it) => (
                      <div
                        key={it.id}
                        className="d-flex justify-content-between align-items-center p-3 rounded-3 border bg-light"
                      >
                        <div className="flex-grow-1 me-2">
                          <div className="fw-semibold text-dark">{it.item_name}</div>
                          <small className="text-muted">
                            {it.source_department_name} • ₹ {Number(it.amount_due).toFixed(2)}
                          </small>
                        </div>
                        <span
                          className="badge"
                          style={{
                            background: it.collected ? "#E6F4EA" : "#FDECEC",
                            color: it.collected ? "#1B7A3D" : "#B3261E",
                          }}
                        >
                          {it.collected
                            ? `Collected ₹${Number(it.collected_amount).toFixed(0)}`
                            : "Pending"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Download Settlement Report: hidden for now — re-enable by removing this comment.
                <button
                  className="btn btn-outline-primary d-flex align-items-center gap-2"
                  onClick={handleDownloadReport}
                  disabled={downloadingReport}
                >
                  {downloadingReport ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <i className="ti ti-download" />
                  )}
                  <span>Download Report (PDF)</span>
                </button>
                */}
              </>
            ) : !settlement?.all_acted ? (
              <WarningBanner
                type="info"
                message="Waiting for all departments to complete their clearance before settlement."
              />
            ) : !settlement?.has_settlement_dept ? (
              <WarningBanner
                type="warning"
                message="No settlement department is configured. Add one (mark it as Settlement) in No-Dues Departments."
              />
            ) : (
              <>
                {/* Sent to Establishment regardless of amount due — a zero-dues
                    case still needs Establishment's own record/sign-off (e.g.
                    confirming deputation remittances), not just a silent
                    auto-finalize. */}
                <div
                  className="d-flex align-items-center gap-2 p-3 mb-3"
                  style={{
                    background: Number(settlement?.total_due || 0) > 0 ? "#FDE8E0" : "#E6F4EA",
                    borderRadius: 10,
                  }}
                >
                  <i
                    className={`ti ${Number(settlement?.total_due || 0) > 0 ? "ti-cash" : "ti-circle-check"}`}
                    style={{ color: Number(settlement?.total_due || 0) > 0 ? "#993C1D" : "#1B7A3D" }}
                  />
                  <span
                    className="fw-semibold"
                    style={{ color: Number(settlement?.total_due || 0) > 0 ? "#993C1D" : "#1B7A3D" }}
                  >
                    {Number(settlement?.total_due || 0) > 0
                      ? `Total recoverable dues: ₹ ${Number(settlement?.total_due || 0).toFixed(2)}`
                      : "No dues to recover — send to Establishment for its own record and sign-off."}
                  </span>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Note to settlement department (optional)
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Any instructions for settlement..."
                    value={settlementComments}
                    onChange={(e) => setSettlementComments(e.target.value)}
                  />
                </div>

                <button
                  className="btn text-white d-flex align-items-center gap-2"
                  style={{ background: "#993C1D", borderRadius: 10, height: 44 }}
                  onClick={handleSendSettlement}
                  disabled={sendingSettlement}
                >
                  {sendingSettlement ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <i className="ti ti-send" />
                  )}
                  <span>Send to Establishment for Further Action</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <DeptClearanceDetailModal
        open={detailOpen}
        deptName={detailDept}
        loading={detailLoading}
        items={detailItems}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
};

export default NoDuesDispatch;
